/**
 * Skill catalogue — management edits the skill list itself: rename, move to a
 * different group, add a new skill, retire one. Retiring keeps historical
 * assessment ratings intact.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Grid3x3, Loader2, Plus, Save, Archive, RotateCcw, Pencil, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from '../../../components/admin/billing/shared';

const field = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none';

const SkillCataloguePage = () => {
  const { authHeader } = useAuth();
  const [skills, setSkills] = useState([]);
  const [groups, setGroups] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [edit, setEdit] = useState(null);   // { id, name, group }
  const [adding, setAdding] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: m }] = await Promise.all([
        axios.get(`${API}/skills/catalogue`, {
          headers: authHeader(), params: { include_inactive: true },
        }),
        axios.get(`${API}/skills/meta`, { headers: authHeader() }),
      ]);
      setSkills(c.skills || []);
      setGroups(m.groups || []);
      setCanEdit(!!m.capabilities?.is_hr);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(edit.id);
    try {
      await axios.patch(`${API}/skills/catalogue/${edit.id}`,
        { name: edit.name, group: edit.group }, { headers: authHeader() });
      toast.success('Skill updated — assessment history is unchanged');
      setEdit(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const create = async () => {
    setBusy('new');
    try {
      await axios.post(`${API}/skills/catalogue`,
        { group: adding.group, name: adding.name }, { headers: authHeader() });
      toast.success(`“${adding.name}” added to ${adding.group}`);
      setAdding(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const setActive = async (s, active) => {
    setBusy(s.id);
    try {
      if (active) {
        await axios.patch(`${API}/skills/catalogue/${s.id}`, { active: true },
          { headers: authHeader() });
        toast.success(`“${s.name}” is back in the matrix`);
      } else {
        await axios.delete(`${API}/skills/catalogue/${s.id}`, { headers: authHeader() });
        toast.success(`“${s.name}” retired — past ratings are kept`);
      }
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-teal-600" /></div>;
  }

  const allGroups = [...new Set([...groups, ...skills.map((s) => s.group)])].filter(Boolean);

  return (
    <div className="space-y-5 pb-20" data-testid="skill-catalogue-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/skills" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1" data-testid="back-to-matrix">
            <ArrowLeft size={14} /> Skill matrix
          </Link>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Grid3x3 size={20} className="text-teal-600" /> Skill catalogue
          </h1>
          <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">
            The rows every assessment is scored against. Rename or move a skill and every
            past rating stays exactly as it was — ratings are stored against the skill, not
            its name. Retiring hides it from new assessments without erasing history.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setAdding({ group: allGroups[0] || '', name: '' })}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-teal-600 text-sm font-medium text-white hover:bg-teal-700"
            data-testid="add-skill-btn"
          >
            <Plus size={15} /> New skill
          </button>
        )}
      </div>

      {adding && (
        <div className="bg-white border border-teal-200 rounded-xl p-4 space-y-3" data-testid="new-skill-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Group</label>
              <input
                list="skill-groups" className={`mt-0.5 ${field}`} value={adding.group}
                onChange={(e) => setAdding((p) => ({ ...p, group: e.target.value }))}
                placeholder="e.g. Polishing" data-testid="new-skill-group"
              />
              <datalist id="skill-groups">
                {allGroups.map((g) => <option key={g} value={g} />)}
              </datalist>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Skill name</label>
              <input
                className={`mt-0.5 ${field}`} value={adding.name}
                onChange={(e) => setAdding((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Graphene coating" data-testid="new-skill-name"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={create} disabled={busy === 'new' || !adding.name.trim() || !adding.group.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:bg-slate-300"
              data-testid="save-new-skill-btn"
            >
              {busy === 'new' ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Add skill
            </button>
            <button onClick={() => setAdding(null)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {allGroups.map((group) => {
        const rows = skills.filter((s) => s.group === group);
        if (!rows.length) return null;
        return (
          <div key={group} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`group-${group}`}>
            <p className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
              {group}
            </p>
            <div className="divide-y divide-slate-100">
              {rows.map((s) => (
                <div key={s.id} className="px-4 py-2.5" data-testid={`skill-row-${s.id}`}>
                  {edit?.id === s.id ? (
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="flex-1 min-w-[180px]">
                        <label className="text-[11px] uppercase tracking-wider text-slate-500">Name</label>
                        <input
                          className={`mt-0.5 ${field}`} value={edit.name}
                          onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))}
                          data-testid={`edit-skill-name-${s.id}`}
                        />
                      </div>
                      <div className="w-44">
                        <label className="text-[11px] uppercase tracking-wider text-slate-500">Group</label>
                        <select
                          className={`mt-0.5 ${field}`} value={edit.group}
                          onChange={(e) => setEdit((p) => ({ ...p, group: e.target.value }))}
                          data-testid={`edit-skill-group-${s.id}`}
                        >
                          {allGroups.map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </div>
                      <button
                        onClick={save} disabled={busy === s.id || !edit.name.trim()}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm text-white hover:bg-teal-700 disabled:bg-slate-300"
                        data-testid={`save-skill-${s.id}`}
                      >
                        {busy === s.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                      </button>
                      <button onClick={() => setEdit(null)} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid={`cancel-skill-${s.id}`}>
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${s.active ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
                        {s.name}
                      </span>
                      {!s.active && (
                        <span className="rounded-full bg-slate-100 text-slate-500 text-[11px] px-2 py-0.5">Retired</span>
                      )}
                      {canEdit && (
                        <div className="ml-auto flex items-center gap-1">
                          <button
                            onClick={() => setEdit({ id: s.id, name: s.name, group: s.group })}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-teal-700 hover:bg-teal-50"
                            title="Rename or move" data-testid={`edit-skill-${s.id}`}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => setActive(s, !s.active)} disabled={busy === s.id}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-800 hover:bg-slate-100"
                            title={s.active ? 'Retire this skill' : 'Bring it back'}
                            data-testid={`toggle-skill-${s.id}`}
                          >
                            {busy === s.id ? <Loader2 size={15} className="animate-spin" />
                              : s.active ? <Archive size={15} /> : <RotateCcw size={15} />}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SkillCataloguePage;
